import { useMemo } from 'react';

import { Patient, PatientEntitlementType, PatientEntitlementTypeQuery } from '@dreryk/egabinet-patient-frontend-api';

import { UseCqrsQueryResult, useCqrsSearch } from '@dreryk/command-query-react-common';
import { TranslateFunction, useTranslate } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { patientEntitlementsTranslationKeys } from '../../translations';

import { PatientEntitlementTypeSelectorFormController } from './PatientEntitlementTypeSelectorForm.types';

type EntitlementConfirmationTypesQueryParameters = {
   patientId: Patient['id'];
};

const useCqrsSearchPatientEntitlementType = (
   parameters: EntitlementConfirmationTypesQueryParameters
): UseCqrsQueryResult<PatientEntitlementType[]> => {
   return useCqrsSearch<PatientEntitlementType>(() => {
      const query = new PatientEntitlementTypeQuery();
      query.patientId = parameters.patientId;
      return query;
   });
};

const triggerLabelExtractor = (translate: TranslateFunction) => (
   patientEntitlementType: PatientEntitlementType | undefined
): string => {
   return patientEntitlementType
      ? translate(patientEntitlementsTranslationKeys.patientEntitlements[patientEntitlementType])
      : '';
};

type PatientEntitlementTypeSelectProps = Pick<PatientEntitlementTypeSelectorFormController, 'register'> & {
   readonly patientId: Patient['id'];
   readonly disabled: boolean;
};

export const PatientEntitlementTypeSelect = (props: PatientEntitlementTypeSelectProps): JSX.Element => {
   const translate = useTranslate();
   const controller = props.register('patientEntitlementType', { required: !props.disabled });
   const { data: patientEntitlementTypes } = useCqrsSearchPatientEntitlementType({
      patientId: props.patientId,
   });

   const items = useMemo(() => patientEntitlementTypes ?? [], [patientEntitlementTypes]);

   return (
      <Select<PatientEntitlementType>
         triggerLabelExtractor={triggerLabelExtractor(translate)}
         controller={controller}
         label={patientEntitlementsTranslationKeys.entitlementCategory}
         placeholder={patientEntitlementsTranslationKeys.selectCategory}
         testId={'patientEntitlementType'}
         disabled={props.disabled}
      >
         <Select.List items={items}>
            {(item: PatientEntitlementType) => (
               <Select.Item
                  item={item}
                  testId={item}
                  label={patientEntitlementsTranslationKeys.patientEntitlements[item]}
               />
            )}
         </Select.List>
      </Select>
   );
};
